import { getCompanyIdLogged, getToken, getUser } from '@/utils';

const COOKIE_NAME = 'panel_v4_preferred';
const OPT_OUT_COOKIE_NAME = 'panel_v4_opt_out';
const COOKIE_MAX_AGE_DAYS = 365;

export const PANEL_V4_OPT_OUT_QUERY = 'use_legacy';

function getPreferenceKey() {
  return `${import.meta.env.VITE_NAME}.panelV4Preferred`;
}

function getOptOutKey() {
  return `${import.meta.env.VITE_NAME}.panelV4OptOut`;
}

function getPanelV4BaseUrl() {
  const url = import.meta.env.VITE_URL_PANEL_V4;

  if (!url) return null;

  return String(url).replace(/\/$/, '');
}

function getCookieDomain() {
  const hostname = window.location.hostname;

  if (hostname === 'localhost' || hostname === '127.0.0.1') return '';

  if (hostname.endsWith('siscomge.com.br')) return '; domain=.siscomge.com.br';

  return '';
}

function clearCookie(name) {
  const domain = getCookieDomain();

  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domain}`;
}

function setCookie(name, value, days) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  const domain = getCookieDomain();

  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/${domain}; SameSite=Lax`;
}

function getCookie(name) {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));

  return match ? decodeURIComponent(match[1]) : null;
}

export function clearPanelV4Preference() {
  localStorage.removeItem(getPreferenceKey());
  clearCookie(COOKIE_NAME);
}

export function setPanelV4OptOut() {
  localStorage.setItem(getOptOutKey(), 'true');
  setCookie(OPT_OUT_COOKIE_NAME, '1', COOKIE_MAX_AGE_DAYS);
}

export function clearPanelV4OptOut() {
  localStorage.removeItem(getOptOutKey());
  clearCookie(OPT_OUT_COOKIE_NAME);
}

export function hasPanelV4OptOut() {
  if (localStorage.getItem(getOptOutKey()) === 'true') return true;

  return getCookie(OPT_OUT_COOKIE_NAME) === '1';
}

export function setPanelV4Preference() {
  clearPanelV4OptOut();
  localStorage.setItem(getPreferenceKey(), 'true');
  setCookie(COOKIE_NAME, '1', COOKIE_MAX_AGE_DAYS);
}

export function hasPanelV4Preference() {
  if (localStorage.getItem(getPreferenceKey()) === 'true') return true;

  return getCookie(COOKIE_NAME) === '1';
}

export function isPanelV4OptOutQuery(value) {
  return value === '1' || value === 'true';
}

export function handlePanelV4OptOutQuery(to, next) {
  if (!isPanelV4OptOutQuery(to.query[PANEL_V4_OPT_OUT_QUERY])) return false;

  clearPanelV4Preference();
  setPanelV4OptOut();

  const query = { ...to.query };
  delete query[PANEL_V4_OPT_OUT_QUERY];

  next({
    path: to.path,
    query,
    hash: to.hash,
    replace: true,
  });

  return true;
}

export function getPanelV4Emails() {
  const raw = import.meta.env.VITE_ENABLE_EMAIL_CLIENTS || '';

  return raw
    .split(',')
    .map((email) => email.trim().toLowerCase())
    .filter(Boolean);
}

export function shouldRedirectToPanelV4(email) {
  if (!getPanelV4BaseUrl() || !email) return false;

  return getPanelV4Emails().includes(String(email).trim().toLowerCase());
}

function encodeSession(session) {
  const json = JSON.stringify(session);

  return btoa(unescape(encodeURIComponent(json)));
}

export function redirectToPanelV4Login() {
  const baseUrl = getPanelV4BaseUrl();

  if (!baseUrl) return false;

  window.location.href = `${baseUrl}/login`;

  return true;
}

export function redirectToPanelV4AfterAuth() {
  const baseUrl = getPanelV4BaseUrl();

  if (!baseUrl) return false;

  const token = getToken();

  setPanelV4Preference();

  if (!token) {
    return redirectToPanelV4Login();
  }

  const user = getUser();
  const session = {
    accessToken: token,
    typeToken: 'Bearer',
    user,
    companyId: getCompanyIdLogged() || user?.company?.id || user?.company_id,
  };

  const encoded = encodeURIComponent(encodeSession(session));

  window.location.href = `${baseUrl}/auth/callback#session=${encoded}`;

  return true;
}

export function redirectToPanelV4IfPreferred() {
  if (hasPanelV4OptOut()) return false;

  if (!hasPanelV4Preference()) return false;

  const token = getToken();

  if (token) {
    return redirectToPanelV4AfterAuth();
  }

  return redirectToPanelV4Login();
}

export function redirectToPanelV4ForUser() {
  const user = getUser();

  // E-mails da lista forçada sempre vão para o v4, mesmo com opt-out
  // (cookie/localStorage de "voltar ao legado").
  if (shouldRedirectToPanelV4(user?.email)) {
    return redirectToPanelV4AfterAuth();
  }

  if (hasPanelV4OptOut()) return false;

  if (hasPanelV4Preference()) {
    return redirectToPanelV4IfPreferred();
  }

  return false;
}
